import { Component, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/lib/cart";
import { api } from "@/lib/api";
import AnnouncementBar from "@/components/AnnouncementBar";
import Header from "@/components/Header";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/Product";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Policy from "@/pages/Policy";
import Checkout from "@/pages/Checkout";
import Collection from "@/pages/Collection";
import Admin from "@/pages/Admin";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink p-8 text-center">
          <div>
            <p className="font-display text-4xl tracking-wide text-bone">SOMETHING WENT WRONG</p>
            <p className="mt-3 font-mono text-xs tracking-[0.2em] text-bone/60">PLEASE REFRESH THE PAGE</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function AnalyticsPageView() {
  const { pathname } = useLocation();
  const prev = useRef(pathname);
  useEffect(() => {
    // The head snippets fired the initial PageViews — only route CHANGES fire here.
    // Comparing against the mount-time pathname makes this StrictMode-proof.
    if (prev.current === pathname) return;
    prev.current = pathname;
    if (window.fbq) window.fbq("track", "PageView");
    if (window.gtag)
      window.gtag("event", "page_view", {
        page_title: document.title,
        page_location: window.location.href,
      });
  }, [pathname]);
  return null;
}

const RoutesBody = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/shop" element={<Shop />} />
    <Route path="/product/:slug" element={<ProductPage />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/checkout" element={<Checkout />} />
    <Route path="/shipping" element={<Policy id="shipping" />} />
    <Route path="/returns" element={<Policy id="returns" />} />
    <Route path="/privacy" element={<Policy id="privacy" />} />
    <Route path="/terms" element={<Policy id="terms" />} />
    <Route path="/collections/:slug" element={<Collection />} />
    <Route path="/admin" element={<Admin />} />
  </Routes>
);

function App() {
  useEffect(() => {
    api.get("/").catch(() => {});
  }, []);

  useEffect(() => {
    // Lenis smooth-scroll is a desktop-mouse nicety — skip it entirely on touch
    // devices, where a permanent rAF loop only burns battery and janks scrolling.
    if (!window.matchMedia("(pointer: fine)").matches) return;
    let rafId;
    let lenis;
    const mount = async () => {
      try {
        const mod = await import("lenis");
        lenis = new mod.default({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        const loop = (t) => {
          lenis.raf(t);
          rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
      } catch (e) {
        // lenis optional — native scroll still works
      }
    };
    mount();
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <CartProvider>
          <ScrollToTop />
          <AnalyticsPageView />
          <AnnouncementBar />
          <Header />
          <CartDrawer />
          <main>
            <RoutesBody />
          </main>
          <Footer />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#1E1B24",
                color: "#F7F5F0",
                border: "1px solid #2E2A38",
                borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace",
                fontSize: "12px",
                letterSpacing: "0.05em",
              },
            }}
          />
        </CartProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
